import { Box, Card, Flex, Group, Stack, useMantineTheme } from '@mantine/core';
import type { IntegrationTimeSeries } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { SelectorTarget, SelectType } from '@repo/common/components';
import { Selector } from '@repo/common/components/Selectors/Selector';
import { Text, Title } from '@repo/foundations';
import { memo, useCallback, useMemo } from 'react';
import { useAuthUser } from '../../../../api';
import type { DateRange } from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import DateRangeButtonGroup from '../../../../components/DateRangeButtonGroup/DateRangeButtonGroup';
import { dateFilterOptions } from '../../constants';
import type { IntegrationChartDataPoint, TimeRangeValue } from '../../types';
import { MetricsOverviewStackedLineChart } from './MetricsOverviewStackedLineChart';
import { processTimeseriesData } from './utils';

export const IntegrationsOverviewChart = memo(
	({
		timeseries,
		dateFilter,
		setDateFilter,
		setTimeRangeValue,
		timeRangeValue,
		dateRangeOption,
		setDateRangeOption,
	}: {
		timeseries: IntegrationTimeSeries;
		dateFilter: string;
		setDateFilter: (dateFilter: string) => void;
		setTimeRangeValue: (timeRangeValue: TimeRangeValue) => void;
		timeRangeValue: TimeRangeValue;
		dateRangeOption: string;
		setDateRangeOption: (dateRangeOption: string) => void;
	}) => {
		const { user, workspace } = useAuthUser();
		const theme = useMantineTheme();

		const chartData = useMemo(() => {
			const sortedData = processTimeseriesData(timeseries?.points);

			const mappedData: IntegrationChartDataPoint[] = sortedData.map(
				(point) => {
					const data: IntegrationChartDataPoint = {
						date: point.timestamp,
						completedSync: point.normal_runs,
						failedSync: point.error_runs,
					};

					return data;
				}
			);

			return mappedData;
		}, [timeseries]);

		const channels = useMemo(
			() => [
				{
					label: 'Completed sync',
					x: 'date' as keyof (typeof chartData)[0],
					y: 'completedSync' as keyof (typeof chartData)[0],
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/success/default'),
					zIndex: 20,
				},
				{
					label: 'Failed sync',
					x: 'date' as keyof (typeof chartData)[0],
					y: 'failedSync' as keyof (typeof chartData)[0],
					renderArea: true,
					areaOpacity: 0.3,
					color: theme.other.getColor('icon/critical/default'),
					zIndex: 10,
				},
			],
			[]
		);

		const tooltipDataRenderer = useCallback(
			(dataIndex: number) => {
				const point = chartData[dataIndex];
				if (!point) return null;

				const isRange =
					dateRangeOption === 'week' || dateRangeOption === 'month';
				const nextPoint = chartData[dataIndex + 1];
				const showRange = isRange && nextPoint;

				return (
					<Box p="sm">
						<Text mb="sm" size="sm" color="text/secondary/default">
							{showRange ? (
								<>
									{point.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
									})}
									{' - '}
									{nextPoint.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
								</>
							) : dateFilter === '24h' ? (
								<>
									{point.date.toLocaleDateString([], {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
									})}
									{' at '}
									{point.date.toLocaleTimeString([], {
										hour: '2-digit',
										minute: '2-digit',
									})}
								</>
							) : (
								point.date.toLocaleDateString([], {
									month: 'long',
									day: 'numeric',
									year: 'numeric',
								})
							)}
						</Text>
						<Flex direction="column" gap="xs" mt="sm">
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[0].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Completed sync </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.completedSync}
								</Text>
							</Flex>
							<Flex justify="space-between" gap="sm">
								<Flex gap="xs" align="center">
									<Box
										w={16}
										h={2}
										bg={channels[1].color}
										style={{
											borderRadius: theme.radius.md,
										}}
										aria-hidden="true"
									/>
									<Text size="sm">Failed sync </Text>
								</Flex>
								<Text fw={500} size="sm">
									{point.failedSync}
								</Text>
							</Flex>
						</Flex>
					</Box>
				);
			},
			[chartData, dateRangeOption, dateFilter]
		);

		const handleDateFilterChange = useCallback(
			(values: string[]) => {
				const newDateFilter = values[0] as string;
				setDateFilter(newDateFilter);
				setTimeRangeValue({
					...timeRangeValue,
					timeRange: newDateFilter,
				});
				trackEvent(
					'integrations/overview/date_filter_change',
					{
						date_filter: newDateFilter,
					},
					user,
					workspace!
				);
			},
			[setDateFilter, setTimeRangeValue, timeRangeValue, user, workspace]
		);

		const handleDateRangeChange = useCallback(
			(range: DateRange, optionId: string) => {
				setTimeRangeValue({
					timeRange: dateFilter,
					dateRange: range,
				});
				setDateRangeOption(optionId);
				trackEvent(
					'integrations/overview/date_range_change',
					{
						date_range_option: optionId,
						date_range_from: range.from?.toISOString() ?? '',
						date_range_to: range.to?.toISOString() ?? '',
					},
					user,
					workspace!
				);
			},
			[dateFilter, setTimeRangeValue, setDateRangeOption, user, workspace]
		);

		const getDateFilterItems = useCallback(async () => dateFilterOptions, []);

		const selectedDateFilterLabel = useMemo(
			() =>
				dateFilterOptions.find((item) => item.value === dateFilter)?.label ??
				'Last 24 hours',
			[dateFilter]
		);

		return (
			<Card withBorder style={{ borderRadius: theme.radius.md }}>
				<Stack spacing="md">
					<Group position="apart">
						<Title size="sm" weight="bold">
							Integration status over time
						</Title>
						<Group position="apart" spacing="sm">
							<Selector
								initialValue={[dateFilter]}
								getItems={getDateFilterItems}
								onChange={handleDateFilterChange}
								selectType={SelectType.Single}
								disableSearch
							>
								<SelectorTarget size="md" variant="default">
									{selectedDateFilterLabel}
								</SelectorTarget>
							</Selector>
							<DateRangeButtonGroup
								selectedOptionId={dateRangeOption}
								onChange={handleDateRangeChange}
							/>
						</Group>
					</Group>
					<MetricsOverviewStackedLineChart
						channels={channels}
						chartData={chartData}
						dateFilter={dateFilter}
						tooltipDataRenderer={tooltipDataRenderer}
						dateRangeOption={dateRangeOption}
					/>
				</Stack>
			</Card>
		);
	}
);
